import CodeBlock from '../CodeBlock';

export default function DocsGPUModule() {
  return (
    <section data-docs-section="gpu" className="text-left mb-16">
      <h2 className="text-2xl font-bold text-white mb-2 group">
        <a href="#gpu" className="hover:text-sky-400 transition-colors">GPU Acceleration</a>
        <span className="ml-2 opacity-0 group-hover:opacity-100 text-gray-600 transition-opacity">#</span>
      </h2>
      <p className="text-gray-400 mb-6">
        On any machine with RAPIDS installed, <code className="text-gray-300">import propalgos</code> automatically
        activates <code className="text-gray-300">cudf.pandas</code> &mdash; all pandas operations across the SDK
        and your own code run on the GPU with zero code changes.
      </p>

      {/* gpu_status */}
      <div data-docs-section="gpu-status">
        <h3 className="text-lg font-semibold text-white mb-2">gpu_status()</h3>
        <p className="text-gray-400 text-sm mb-3">
          Check whether GPU acceleration is active and get hardware details.
        </p>
        <CodeBlock
          code={`>>> pa.gpu_status()
{
    'cudf_active': True,
    'gpu_available': True,
    'rapids_version': '24.10',
    'device': 'NVIDIA A100-SXM4-40GB',
    'instance_id': 'i-0abc123def456',
    'cloud_provider': 'aws',
    'region': 'us-east-1',
    'instance_type': 'p3.2xlarge',
    'on_platform': True,
    'python_version': '3.12.13',
    'sdk_version': '0.4.0',
    'warning': None
}`}
        />
        <p className="text-sm text-gray-500 mt-3">
          If <code className="text-gray-400">cudf_active</code> is <code className="text-gray-400">False</code> and{' '}
          <code className="text-gray-400">gpu_available</code> is <code className="text-gray-400">True</code>,
          the <code className="text-gray-400">warning</code> field explains why.
        </p>
      </div>

      {/* What runs on GPU */}
      <div className="mt-8" data-docs-section="gpu-what-runs">
        <h3 className="text-lg font-semibold text-white mb-3">What runs on GPU</h3>
        <div className="space-y-3">
          {[
            {
              title: 'Data loading',
              desc: 'pa.data.load() and CryptoParquetProvider use pd.read_parquet() which cudf.pandas dispatches to GPU transparently.',
            },
            {
              title: 'Data staging',
              desc: 'FUSE-mounted parquets are staged to local NVMe via kvikIO (GDS zero-copy) when available, falling back to a 16 MB buffered copy.',
            },
            {
              title: 'Signal generation',
              desc: 'All signal functions use vectorized pandas ops (.rolling(), .ewm(), .clip(), boolean indexing) that map directly to cuDF GPU kernels.',
            },
            {
              title: 'Multi-symbol ops',
              desc: 'rsi_stacked() uses boundary-nulling to compute RSI across all symbols in a single vectorized pass — no groupby().apply().',
            },
            {
              title: 'Backtesting',
              desc: 'The entire backtest engine is vectorized: .pct_change(), .cumprod(), .diff(), scalar arithmetic. No lambdas or row-level loops.',
            },
            {
              title: 'Factor computation',
              desc: 'All risk and performance metrics use GPU-native pandas methods — .quantile() instead of np.percentile(), .cumprod(), .cummax().',
            },
            {
              title: 'Visualization',
              desc: 'Aggregations compute on GPU before transferring to CPU for Plotly rendering. Monthly returns use vectorized resample().prod().',
            },
          ].map((item) => (
            <div key={item.title} className="flex gap-3">
              <span className="text-emerald-400 mt-0.5 shrink-0">&#x25CF;</span>
              <p className="text-sm text-gray-400">
                <span className="text-gray-200 font-medium">{item.title}</span> &mdash; {item.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Auto-activation */}
      <div className="mt-8" data-docs-section="gpu-auto">
        <h3 className="text-lg font-semibold text-white mb-2">Auto-activation</h3>
        <p className="text-gray-400 text-sm mb-3">
          On PropAlgos GPU instances, RAPIDS is pre-installed and cudf.pandas activates on import.
          No <code className="text-gray-300">%load_ext</code> or manual setup needed.
        </p>
        <CodeBlock
          code={`import propalgos as pa
import pandas as pd

# Both use GPU transparently
prices = pa.data.prices("SPY", start="2020-01-01")
df = pd.read_parquet("my_data.parquet")  # also GPU-accelerated`}
        />
      </div>

      {/* Opt out */}
      <div className="mt-6 p-4 bg-gray-900/50 border border-gray-800 rounded-lg space-y-3">
        <p className="text-sm text-gray-400">
          <span className="text-amber-400 font-medium">Opt out</span> (for benchmarking or debugging):
        </p>
        <CodeBlock
          code="export PROPALGOS_CUDF_AUTO=0"
          language="bash"
        />
        <p className="text-sm text-gray-400">
          <span className="text-sky-400 font-medium">Off-platform</span> (local dev, Colab) &mdash; activate
          manually before importing:
        </p>
        <CodeBlock
          code={`%load_ext cudf.pandas
import propalgos as pa`}
        />
      </div>

      {/* Writing GPU-compatible code */}
      <div className="mt-8" data-docs-section="gpu-compat">
        <h3 className="text-lg font-semibold text-white mb-3">Writing GPU-compatible code</h3>
        <p className="text-gray-400 text-sm mb-4">
          Follow these rules so cudf.pandas can accelerate your notebook code:
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="py-2 pr-4 text-gray-400 font-medium">Do</th>
                <th className="py-2 text-gray-400 font-medium">Don&apos;t</th>
              </tr>
            </thead>
            <tbody className="text-gray-400">
              <tr className="border-b border-gray-800/50">
                <td className="py-2.5 pr-4"><code className="text-emerald-400">np.log(series)</code></td>
                <td className="py-2.5"><code className="text-red-400">series.apply(np.log)</code></td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-2.5 pr-4"><code className="text-emerald-400">series.quantile(0.95)</code></td>
                <td className="py-2.5"><code className="text-red-400">np.percentile(series, 95)</code></td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-2.5 pr-4"><code className="text-emerald-400">result[s &gt; 0] = 1</code></td>
                <td className="py-2.5"><code className="text-red-400">s.apply(lambda x: 1 if x &gt; 0 else 0)</code></td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-2.5 pr-4"><code className="text-emerald-400">ewm(com=13)</code></td>
                <td className="py-2.5"><code className="text-red-400">ewm(com=13, min_periods=14)</code></td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-2.5 pr-4"><code className="text-emerald-400">pd.read_parquet(path)</code></td>
                <td className="py-2.5"><code className="text-red-400">cudf.read_parquet(path)</code></td>
              </tr>
              <tr>
                <td className="py-2.5 pr-4"><code className="text-emerald-400">list(series)</code></td>
                <td className="py-2.5"><code className="text-red-400">series.tolist()</code></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
